import type { OnDestroy } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Input,
  output,
  inject,
  input,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { SafeResourceUrl } from '@angular/platform-browser';
import { DomSanitizer } from '@angular/platform-browser';

import { iframeResizer } from 'iframe-resizer';
import { take } from 'rxjs';

import { injectPdfViewerConfig, injectPdfViewerErrorContexts } from '@core/pdf-viewer-contracts';

import { PdfViewerConfigNotProvidedError } from './pdf-viewer-config-not-provided-error';

@Component({
  selector: 'rt-pdf-viewer',
  templateUrl: './pdf-viewer.component.html',
  styleUrls: ['./pdf-viewer.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [],
})
export class PdfViewerComponent implements OnDestroy {
  public static ngAcceptInputType_assetUrl: string | SafeResourceUrl;

  private readonly sanitizer = inject(DomSanitizer);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly pdfViewerErrorContexts = injectPdfViewerErrorContexts({ optional: true });
  protected readonly config = injectPdfViewerConfig({ optional: true });

  public readonly minHeight = input<string>('700px');

  // https://github.com/davidjbradshaw/iframe-resizer/blob/master/docs/parent_page/options.md#heightcalculationmethod
  public readonly heightCalculationMethod = input<string>('lowestElement');

  @Input()
  public set assetUrl(url: SafeResourceUrl) {
    this.safeAssetUrl = this.getViewerUrl(url);
  }

  public get assetUrl(): SafeResourceUrl {
    return this.safeAssetUrl;
  }

  public CDNHost: string = '';
  public viewerDirectory: string = '';
  public iframeLoad = output<HTMLIFrameElement>();

  private safeAssetUrl: SafeResourceUrl;

  private iframes: IFrameResizerFrame[] = [];

  constructor() {
    if (this.config && this.config.CDNHost && this.config.viewerDirectory) {
      this.CDNHost = this.config.CDNHost;
      this.viewerDirectory = this.config.viewerDirectory;
    } else {
      throw new PdfViewerConfigNotProvidedError();
    }
  }

  public onIframeLoad(frame: HTMLIFrameElement): void {
    this.iframes = iframeResizer(
      {
        heightCalculationMethod: this.heightCalculationMethod(),
        checkOrigin: [this.CDNHost],
        scrolling: 'omit',
        tolerance: 100,
        minHeight: this.minHeight(),
        resizeFrom: 'child',
      },
      frame,
    );

    if (this.pdfViewerErrorContexts) {
      this.pdfViewerErrorContexts
        .pipe(take(1), takeUntilDestroyed(this.destroyRef))
        .subscribe(pdfViewerErrorContexts => {
          this.sendDataToFrame(frame, pdfViewerErrorContexts!);
        });
    }

    this.iframeLoad.emit(frame);
  }

  public ngOnDestroy(): void {
    this.iframes.forEach(frame => frame.iFrameResizer.close());
  }

  private getViewerUrl(assetUrl: SafeResourceUrl): SafeResourceUrl {
    // eslint-disable-next-line ban/ban
    return this.sanitizer.bypassSecurityTrustResourceUrl(
      `${this.CDNHost}/${this.viewerDirectory}/web/viewer.html?file=${assetUrl}#pagemode=none`,
    );
  }

  private sendDataToFrame(frame: HTMLIFrameElement, data: object): void {
    frame.contentWindow?.postMessage({ kind: 'RtPdfViewerData', ...data }, this.CDNHost);
  }
}
